import { getTableConfig } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import { ACTOR_TABLE_PARENTS } from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import * as actorSchema from "#schema";

export type ActorTableKey = keyof typeof actorSchema;

/**
 * Every actor table (by its `actorSchema` key), parents before the tables whose rows reference
 * them. Derived from the declared parents (`ACTOR_TABLE_PARENTS`; the schema has no foreign
 * keys), so a new table is included without any list here having to change. Tables with no order
 * between them stay alphabetical.
 *
 * Use it wherever rows of several tables are written together: insert in this order, delete in the
 * reverse order.
 */
export function listActorTableKeysParentsFirst(): ActorTableKey[] {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- `Object.keys` of the schema module
  const keys = (Object.keys(actorSchema) as ActorTableKey[]).sort();
  const keyByTableName = new Map(keys.map((key) => [getTableConfig(actorSchema[key]).name, key]));
  const parentsByTable: Partial<Record<string, readonly { column: string; table: string }[]>> =
    ACTOR_TABLE_PARENTS;
  const parentKeysByKey = new Map(
    keys.map((key) => [
      key,
      (parentsByTable[getTableConfig(actorSchema[key]).name] ?? []).flatMap((parent) => {
        const parentKey = keyByTableName.get(parent.table);
        if (parentKey === undefined) {
          throw new Error(`Actor table ${key} references a table outside the actor schema`);
        }
        // A table that references itself has no order to resolve.
        return parentKey === key ? [] : [parentKey];
      }),
    ]),
  );

  const ordered: ActorTableKey[] = [];
  const placed = new Set<ActorTableKey>();
  while (ordered.length < keys.length) {
    const ready = keys.filter(
      (key) =>
        !placed.has(key) && (parentKeysByKey.get(key) ?? []).every((parent) => placed.has(parent)),
    );
    if (ready.length === 0) {
      throw new Error("Actor tables reference each other in a cycle");
    }
    for (const key of ready) {
      ordered.push(key);
      placed.add(key);
    }
  }
  return ordered;
}
